import React from 'react';
import {
  Row, Col, Form,
} from 'antd';
import StyledDatePicker from '../../CustomAntdComponents/StyledDatePickerB2C/DatePicker';
import translations from '../../../screens/Search/translations';
import { useI18nContext } from '../../../utils/I18n';
import { getDisabledDateRange, getMultiDestinationDate, toDate } from '../../../utils/helpers';
import {AirportFieldsWrapper, DepartureWrapper, SwitcherWrapper} from "../styles";
import AirportSelector from "../../RouteSelectorB2C";
import {ReactComponent as DirectionIcon} from "../../../assets/images/direction-hr.svg";
import {ReactComponent as VerticalDirectionIcon} from "../../../assets/images/direction.svg";
import useIsMobile from "../../../hooks/useIsMobile";
import {StyledParagraph} from "../../CustomAntdComponents/Typography";
import moment from 'moment';
import { getMinSelectableMultiCityDate, getMultiCityDisabledDate } from '../helper';

type Props = {
  field: any,
  form: any,
  airportOptions?: any,
  limitSuggestionHeight?: boolean,
}
export function FlightSearchForm({
  airportOptions, field, form, limitSuggestionHeight
}: Props) {
  const {
    state: { t },
  } = useI18nContext();
  const { isMobile } = useIsMobile();

  const validateDestination = (arg: any, value) => {
    if (!value) {
      return Promise.reject(new Error(t(translations).validations.destination));
    }

    if(value === form.getFieldValue(`trips[${field.key}].OriginLocationCode`)) {
        return Promise.reject(new Error(t(translations).validations.invalidDestination));
    }

    return Promise.resolve();
  };

  const validateDeparture = (arg: any, value) => {
    if (!value) {
      return Promise.reject(new Error(t(translations).validations.departure));
    }

    if(!form.getFieldValue(`trips[${field.key}].DestinationLocationCode`)) {
        return Promise.resolve();
    }

    if(value === form.getFieldValue(`trips[${field.key}].DestinationLocationCode`)) {
        return Promise.reject(new Error(t(translations).validations.invalidDeparture));
    }

    return Promise.resolve();
  };

  const handleSwitchAirports = () => {
    const trips = form.getFieldValue("trips");
    const tripIndex = field.key;

    const origin = form.getFieldValue(`trips[${field.key}].OriginLocationCode`) || form.getFieldValue('trips')[field.key].OriginLocationCode;
    const destination = form.getFieldValue(`trips[${field.key}].DestinationLocationCode`) || form.getFieldValue('trips')[field.key].DestinationLocationCode;

    trips[tripIndex] = {
      ...trips[tripIndex],
      OriginLocationCode: destination,
      DestinationLocationCode: origin,
    }

    form.setFieldValue("trips", trips);
    form.setFieldValue(`trips[${field.key}].OriginLocationCode`, destination);
    form.setFieldValue(`trips[${field.key}].DestinationLocationCode`, origin);
  }

  const departureValue = form.getFieldValue([
  "trips",
  field.name,
  "DepartureDate",
]);

const selectedRange = {
  from: departureValue?.from
    ? new Date(departureValue.from)
    : undefined,
  to: departureValue?.to
    ? new Date(departureValue.to)
    : undefined,
};
  

  return (
      <Row gutter={8}>
        <Col xs={24} lg={16}>
          <AirportFieldsWrapper>
            <Row gutter={[16, 0]}>
              <Col xs={24} lg={0}>
                <div style={{ marginBottom: "10px" }}>
                  <StyledParagraph size="normal">
                    {`Flight ${field.key + 1}`}
                  </StyledParagraph>
                </div>
              </Col>
              <Col xs={24} lg={12}>
                <DepartureWrapper>
                  <Form.Item
                      {...field}
                      name={[field.name, 'OriginLocationCode']}
                      fieldKey={[field.key, 'OriginLocationCode']}
                      key={`OriginLocationCode${field.key}`}
                      rules={[
                        {
                          validator: validateDeparture,
                        }
                      ]}
                  >
                      <AirportSelector
                          label={t(translations).inputs.fromLabel}
                          placeholder={t(translations).inputs.fromPlaceholder}
                          icon="takeoff"
                          value={form.getFieldValue(`trips[${field.name}].OriginLocationCode`)}
                          options={airportOptions}
                          onChange={(value) => {
                              form.setFieldValue(`trips[${field.key}].OriginLocationCode`, value);
                          }}
                          tripType="flight"
                    limitSuggestionHeight={limitSuggestionHeight}
                    mode="from"
                      />
                  </Form.Item>
                  <SwitcherWrapper onClick={handleSwitchAirports}>
                    {isMobile ? <VerticalDirectionIcon /> :<DirectionIcon />}
                  </SwitcherWrapper>
                </DepartureWrapper>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item
                    {...field}
                    name={[field.name, 'DestinationLocationCode']}
                    fieldKey={[field.key, 'DestinationLocationCode']}
                    dependencies={[field.name, 'OriginLocationCode']}
                    rules={[
                      {
                        validator: validateDestination,
                      },
                    ]}
                >
                  <AirportSelector
                      label={t(translations).inputs.toLabel}
                      placeholder={t(translations).inputs.toPlaceholder}
                      options={airportOptions}
                      value={form.getFieldValue(`trips[${field.name}].DestinationLocationCode`)}
                      onChange={(value) => form.setFieldValue(`trips[${field.key}].DestinationLocationCode`, value)}
                      icon="land"
                      tripType="flight"
                  limitSuggestionHeight={limitSuggestionHeight}
                  mode="to"
                  />
                </Form.Item>
              </Col>
            </Row>
          </AirportFieldsWrapper>
        </Col>

        <Col xs={24} lg={8}>
  <Form.Item
    name={[field.name, "DepartureDate"]}
    rules={[
      {
        required: true,
        message: t(translations).validations.departureDate,
      },
    ]}
  >
    <Form.Item shouldUpdate noStyle>
      {() => {
        const departureValue = form.getFieldValue([
          "trips",
          field.name,
          "DepartureDate",
        ]);

        const rawValue =
          departureValue?.from ??
          departureValue?.value ??
          departureValue;

        const selectedRange = {
          from: getMultiDestinationDate(departureValue),

          to: undefined,
        };
              
        const minSelectableDate = getMinSelectableMultiCityDate(form, field.name);

        return (
          <StyledDatePicker
            label={t(translations).inputs.departureDate}
            value={departureValue}
            disabledDate={(current) =>
              getMultiCityDisabledDate(current, form, field.name, getDisabledDateRange)
            }
            minDate={minSelectableDate}
            selectedRange={selectedRange}
            singleDateOnly
            isMulti
            setSelectedRange={(range) => {
              form.setFieldValue(
                ["trips", field.name, "DepartureDate"],
                range
                  ? {
                      from: range.from,
                      to: undefined,
                    }
                  : undefined
              );
            }}
          />
        );
      }}
    </Form.Item>
  </Form.Item>
</Col>
      </Row>
  );
}
